import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getToken } from '@shared/services/api';
import { Card, Badge, Button } from '@shared/components/ui';
import { getStatusTone, toneToBadgeVariant } from '@shared/config/theme';
import {
  History,
  Download,
  Filter,
  Shield,
  Database,
  Key,
  User as UserIcon,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Columns3,
} from 'lucide-react';

interface AuditLog {
  id: string;
  user_email?: string;
  user_name?: string;
  action: string;
  category: string;
  ip_address?: string;
  success: boolean;
  error_message?: string;
  created_at: string;
  details?: Record<string, any>;
}

interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
}

const DEFAULT_COLUMNS: ColumnConfig[] = [
  { key: 'timestamp', label: 'Timestamp', visible: true },
  { key: 'actor', label: 'Actor', visible: true },
  { key: 'action', label: 'Action', visible: true },
  { key: 'status', label: 'Status', visible: true },
  { key: 'context', label: 'Context', visible: true },
  { key: 'category', label: 'Category', visible: false },
  { key: 'ip_address', label: 'IP Address', visible: false },
];

export default function AuditLogs() {
  const { t, i18n } = useTranslation('auditLogs');
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [exportError, setExportError] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dateRangeFilter, setDateRangeFilter] = useState('');
  const [exporting, setExporting] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [columns, setColumns] = useState<ColumnConfig[]>(() => {
    const saved = localStorage.getItem('auditLogColumns');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return DEFAULT_COLUMNS;
      }
    }
    return DEFAULT_COLUMNS;
  });
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const columnPickerRef = useRef<HTMLDivElement>(null);

  const visibleColumns = columns.filter((c) => c.visible);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnPickerRef.current && !columnPickerRef.current.contains(event.target as Node)) {
        setShowColumnPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchAuditLogs();
  }, [categoryFilter]);

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const token = getToken();
      let url = '/api/v1/audit/logs?limit=100';
      if (categoryFilter) url += `&category=${categoryFilter}`;

      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to load audit logs');
      const data = await res.json();
      setLogs(Array.isArray(data.logs) ? data.logs : []);
      setLoadError(false);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = getToken();
      const params = new URLSearchParams({ format: 'csv' });
      if (categoryFilter) params.set('category', categoryFilter);
      if (dateRangeFilter) {
        const start = new Date();
        start.setDate(start.getDate() - Number(dateRangeFilter));
        params.set('start_date', start.toISOString().slice(0, 10));
        params.set('end_date', new Date().toISOString().slice(0, 10));
      }
      const res = await fetch(`/api/v1/logs/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to export audit logs');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setExportError(false);
    } catch {
      setExportError(true);
    } finally {
      setExporting(false);
    }
  };

  const getCategoryIcon = (cat: string) => {
    switch (cat.toLowerCase()) {
      case 'authentication':
        return <Key size={14} />;
      case 'backup':
        return <Database size={14} />;
      case 'system':
      case 'security':
        return <Shield size={14} />;
      default:
        return <UserIcon size={14} />;
    }
  };

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const toggleColumn = (key: string) => {
    const updated = columns.map((col) =>
      col.key === key ? { ...col, visible: !col.visible } : col
    );
    setColumns(updated);
    localStorage.setItem('auditLogColumns', JSON.stringify(updated));
  };

  const filteredLogs = logs.filter((log) => {
    if (!dateRangeFilter) return true;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - Number(dateRangeFilter));
    return new Date(log.created_at).getTime() >= cutoff.getTime();
  });

  const getActionLabel = (action: string) => {
    const key = action
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
    const fallback = action
      .replace(/[._]/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
    return t(`actions.${key}`, { defaultValue: fallback });
  };

  const getCategoryLabel = (category: string) => {
    const key: Record<string, string> = {
      access: 'access',
      admin_access: 'adminAccess',
      authentication: 'authentication',
      backup: 'backup',
      system: 'system',
      security: 'security',
    };
    return key[category.toLowerCase()]
      ? t(`categories.${key[category.toLowerCase()]}`)
      : category.replace(/_/g, ' ');
  };

  const getColumnLabel = (column: ColumnConfig) => {
    const key = column.key === 'ip_address' ? 'ipAddress' : column.key;
    return t(`columns.${key}`);
  };

  const getDetailLabel = (field: string) => {
    const key = field.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
    const fallback = key.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
    const translationKey = `details.fields.${key}`;
    const translated = t(translationKey);
    return translated === translationKey ? fallback : translated;
  };

  const getDetailValue = (field: string, value: unknown) => {
    const key = field.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
    if (value == null || value === '') return t('details.empty');

    if (typeof value === 'string') {
      const normalizedValue = value.toLowerCase();
      const enumKey =
        key === 'status'
          ? `details.values.status.${normalizedValue}`
          : key === 'access_type' && normalizedValue === 'web_ui'
            ? 'details.values.accessType.webUi'
            : key === 'backup_type' && normalizedValue === 'files'
              ? 'details.values.backupType.files'
              : key === 'command' && normalizedValue === 'backup_command_sent'
                ? 'details.values.commands.backupCommandSent'
                : null;
      return enumKey ? t(enumKey, { defaultValue: value }) : value;
    }

    if (key === 'changed_fields' && Array.isArray(value)) {
      return value.map((fieldName) => getDetailLabel(String(fieldName))).join(', ');
    }
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const getDateTime = (value: string) => {
    const date = new Date(value);
    return {
      date: date.toLocaleDateString(i18n.resolvedLanguage),
      time: date.toLocaleTimeString(i18n.resolvedLanguage),
    };
  };

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="app-page-title">{t('title')}</h1>
            <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-1">
              {t('subtitle')}
            </p>
          </div>
          <Button
            onClick={handleExport}
            isLoading={exporting}
            size="lg"
            className="shadow-brand-200"
          >
            <Download className="w-5 h-5 mr-2" strokeWidth={3} />{' '}
            {exporting ? t('exporting') : t('exportCsv')}
          </Button>
        </div>
        {exportError && (
          <p
            role="alert"
            className="text-sm font-semibold text-state-danger-light dark:text-state-danger-dark"
          >
            {t('exportFailed')}
          </p>
        )}

        {/* Filters */}
        <Card className="p-4 flex flex-col md:flex-row gap-4 items-center bg-surface-card-light dark:bg-surface-card-dark">
          <div className="flex-1 w-full relative">
            <Filter
              size={16}
              className="absolute left-3.5 top-3.5 text-text-subtle-light dark:text-text-subtle-dark"
            />
            <select
              aria-label={t('categories.all')}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="app-field-control min-h-11 w-full pl-10"
            >
              <option value="">{t('categories.all')}</option>
              <option value="access">{t('categories.access')}</option>
              <option value="admin_access">{t('categories.adminAccess')}</option>
              <option value="authentication">{t('categories.authentication')}</option>
              <option value="backup">{t('categories.backup')}</option>
              <option value="security">{t('categories.security')}</option>
              <option value="system">{t('categories.system')}</option>
            </select>
          </div>
          <div className="flex-1 w-full relative">
            <History
              size={16}
              className="absolute left-3.5 top-3.5 text-text-subtle-light dark:text-text-subtle-dark"
            />
            <select
              aria-label={t('timeRanges.all')}
              value={dateRangeFilter}
              onChange={(e) => setDateRangeFilter(e.target.value)}
              className="app-field-control min-h-11 w-full pl-10"
            >
              <option value="">{t('timeRanges.all')}</option>
              <option value="7">{t('timeRanges.last7Days')}</option>
              <option value="30">{t('timeRanges.last30Days')}</option>
              <option value="90">{t('timeRanges.last90Days')}</option>
            </select>
          </div>
          <div className="relative" ref={columnPickerRef}>
            <button
              onClick={() => setShowColumnPicker(!showColumnPicker)}
              aria-expanded={showColumnPicker}
              aria-haspopup="true"
              className="flex min-h-11 items-center gap-2 rounded-xl bg-surface-2-light px-4 text-sm font-bold text-slate-600 transition-colors hover:bg-surface-border-light dark:bg-surface-2-dark dark:text-slate-300 dark:hover:bg-surface-border-dark"
            >
              <Columns3 size={16} />
              {t('columns.label')}
              <ChevronDown
                size={14}
                className={`transition-transform ${showColumnPicker ? 'rotate-180' : ''}`}
              />
            </button>
            {showColumnPicker && (
              <div className="absolute right-0 mt-2 w-56 bg-surface-card-light dark:bg-surface-card-dark rounded-xl shadow-lg border border-surface-border-light dark:border-surface-border-dark z-50 py-2">
                {columns.map((col) => (
                  <label
                    key={col.key}
                    className="flex min-h-11 items-center gap-3 px-4 py-2 hover:bg-surface-2-light dark:hover:bg-surface-2-dark cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={col.visible}
                      onChange={() => toggleColumn(col.key)}
                      className="w-4 h-4 rounded border-border-strong-light dark:border-border-strong-dark text-brand-600 dark:text-brand-400 focus:ring-brand-500 dark:focus:ring-brand-400"
                    />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {getColumnLabel(col)}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </Card>

        {loading ? (
          <div className="py-20 flex justify-center">
            <div className="animate-spin h-12 w-12 border-b-2 border-brand-600 rounded-full"></div>
          </div>
        ) : loadError ? (
          <Card className="py-16 text-center">
            <p className="font-semibold text-state-danger-light dark:text-state-danger-dark">
              {t('states.loadError')}
            </p>
            <Button variant="outline" className="mt-4 min-h-11" onClick={fetchAuditLogs}>
              {t('retry')}
            </Button>
          </Card>
        ) : filteredLogs.length === 0 ? (
          <Card className="py-16 text-center text-text-muted-light dark:text-text-muted-dark">
            {t('states.empty')}
          </Card>
        ) : (
          <>
            <div className="hidden overflow-hidden rounded-2xl border border-surface-border-light bg-surface-card-light shadow-sm dark:border-surface-border-dark dark:bg-surface-card-dark lg:block">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-surface-border-light dark:border-surface-border-dark bg-surface-2-light dark:bg-surface-2-dark">
                      {visibleColumns.find((c) => c.key === 'timestamp') && (
                        <th className="p-4 text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
                          {t('columns.timestamp')}
                        </th>
                      )}
                      {visibleColumns.find((c) => c.key === 'actor') && (
                        <th className="p-4 text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
                          {t('columns.actor')}
                        </th>
                      )}
                      {visibleColumns.find((c) => c.key === 'action') && (
                        <th className="p-4 text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
                          {t('columns.action')}
                        </th>
                      )}
                      {visibleColumns.find((c) => c.key === 'status') && (
                        <th className="p-4 text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
                          {t('columns.status')}
                        </th>
                      )}
                      {visibleColumns.find((c) => c.key === 'context') && (
                        <th className="p-4 text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
                          {t('columns.context')}
                        </th>
                      )}
                      {visibleColumns.find((c) => c.key === 'category') && (
                        <th className="p-4 text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
                          {t('columns.category')}
                        </th>
                      )}
                      {visibleColumns.find((c) => c.key === 'ip_address') && (
                        <th className="p-4 text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
                          {t('columns.ipAddress')}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-border-light dark:divide-surface-border-dark">
                    {filteredLogs.map((log) => (
                      <tr
                        key={log.id}
                        className="hover:bg-surface-2-light dark:hover:bg-surface-2-dark transition-colors"
                      >
                        {visibleColumns.find((c) => c.key === 'timestamp') && (
                          <td className="p-4">
                            <div className="text-xs font-black text-slate-900 dark:text-white uppercase">
                              {getDateTime(log.created_at).date}
                            </div>
                            <div className="text-[10px] text-text-subtle-light dark:text-text-subtle-dark font-mono">
                              {getDateTime(log.created_at).time}
                            </div>
                          </td>
                        )}
                        {visibleColumns.find((c) => c.key === 'actor') && (
                          <td className="p-4">
                            <div className="font-bold text-slate-700 dark:text-slate-300 text-sm">
                              {log.user_name || log.user_email || t('systemActor')}
                            </div>
                            {log.user_name && log.user_email && (
                              <div className="text-[10px] text-text-subtle-light dark:text-text-subtle-dark font-medium">
                                {log.user_email}
                              </div>
                            )}
                          </td>
                        )}
                        {visibleColumns.find((c) => c.key === 'action') && (
                          <td className="p-4">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="p-1 rounded bg-surface-2-light dark:bg-surface-2-dark text-text-muted-light dark:text-text-muted-dark">
                                {getCategoryIcon(log.category)}
                              </span>
                              <span className="text-xs font-bold text-slate-900 dark:text-white">
                                {getActionLabel(log.action)}
                              </span>
                            </div>
                          </td>
                        )}
                        {visibleColumns.find((c) => c.key === 'status') && (
                          <td className="p-4">
                            <Badge
                              variant={
                                toneToBadgeVariant[
                                  getStatusTone(log.success ? 'COMPLETED' : 'FAILED')
                                ]
                              }
                              className="text-[10px]"
                            >
                              {log.success ? t('statuses.completed') : t('statuses.denied')}
                            </Badge>
                          </td>
                        )}
                        {visibleColumns.find((c) => c.key === 'context') && (
                          <td className="p-4 max-w-[350px]">
                            {log.error_message ? (
                              <div className="flex items-center gap-1.5 text-state-danger-light dark:text-state-danger-dark text-xs font-medium">
                                <AlertCircle size={12} /> {log.error_message}
                              </div>
                            ) : log.details ? (
                              <div>
                                <button
                                  onClick={() => toggleRow(log.id)}
                                  aria-expanded={expandedRows.has(log.id)}
                                  className="flex min-h-11 items-center gap-1 text-xs text-text-muted-light transition-colors hover:text-brand-600 dark:text-text-muted-dark dark:hover:text-brand-400"
                                >
                                  {expandedRows.has(log.id) ? (
                                    <ChevronDown size={14} />
                                  ) : (
                                    <ChevronRight size={14} />
                                  )}
                                  {t(expandedRows.has(log.id) ? 'details.hide' : 'details.show', {
                                    count: Object.keys(log.details).length,
                                  })}
                                </button>
                                {expandedRows.has(log.id) && (
                                  <div className="mt-2 space-y-1 bg-surface-2-light dark:bg-surface-2-dark rounded-lg p-2">
                                    {Object.entries(log.details).map(([key, value]) => (
                                      <div key={key} className="text-xs">
                                        <span className="text-text-subtle-light dark:text-text-subtle-dark font-medium">
                                          {getDetailLabel(key)}:
                                        </span>{' '}
                                        <span className="text-slate-600 dark:text-slate-300 font-medium break-all">
                                          {getDetailValue(key, value)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-xs text-text-muted-light dark:text-text-muted-dark font-medium">
                                -
                              </div>
                            )}
                          </td>
                        )}
                        {visibleColumns.find((c) => c.key === 'category') && (
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <span className="p-1 rounded bg-surface-2-light dark:bg-surface-2-dark text-text-muted-light dark:text-text-muted-dark">
                                {getCategoryIcon(log.category)}
                              </span>
                              <span className="text-xs font-medium text-slate-600 dark:text-text-subtle-dark">
                                {getCategoryLabel(log.category)}
                              </span>
                            </div>
                          </td>
                        )}
                        {visibleColumns.find((c) => c.key === 'ip_address') && (
                          <td className="p-4">
                            <span className="text-xs font-mono text-text-muted-light dark:text-text-muted-dark">
                              {log.ip_address || '-'}
                            </span>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="space-y-3 lg:hidden">
              {filteredLogs.map((log) => {
                const dateTime = getDateTime(log.created_at);
                const isExpanded = expandedRows.has(log.id);
                const detailCount = Object.keys(log.details ?? {}).length;
                return (
                  <Card key={log.id} className="space-y-3 p-4">
                    {visibleColumns.some((column) => column.key === 'timestamp') && (
                      <div className="flex justify-between gap-4 text-xs text-text-muted-light dark:text-text-muted-dark">
                        <span>{t('columns.timestamp')}</span>
                        <span className="text-right font-medium">
                          {dateTime.date} · {dateTime.time}
                        </span>
                      </div>
                    )}
                    {visibleColumns.some((column) => column.key === 'actor') && (
                      <div className="flex justify-between gap-4 text-sm">
                        <span className="text-text-muted-light dark:text-text-muted-dark">
                          {t('columns.actor')}
                        </span>
                        <span className="break-all text-right font-semibold">
                          {log.user_name || log.user_email || t('systemActor')}
                        </span>
                      </div>
                    )}
                    {visibleColumns.some((column) => column.key === 'action') && (
                      <div className="flex justify-between gap-4 text-sm">
                        <span className="text-text-muted-light dark:text-text-muted-dark">
                          {t('columns.action')}
                        </span>
                        <span className="text-right font-semibold">
                          {getActionLabel(log.action)}
                        </span>
                      </div>
                    )}
                    {visibleColumns.some((column) => column.key === 'status') && (
                      <div className="flex justify-between gap-4 text-sm">
                        <span className="text-text-muted-light dark:text-text-muted-dark">
                          {t('columns.status')}
                        </span>
                        <Badge
                          variant={
                            toneToBadgeVariant[getStatusTone(log.success ? 'COMPLETED' : 'FAILED')]
                          }
                        >
                          {log.success ? t('statuses.completed') : t('statuses.denied')}
                        </Badge>
                      </div>
                    )}
                    {visibleColumns.some((column) => column.key === 'category') && (
                      <div className="flex justify-between gap-4 text-sm">
                        <span className="text-text-muted-light dark:text-text-muted-dark">
                          {t('columns.category')}
                        </span>
                        <span className="text-right">{getCategoryLabel(log.category)}</span>
                      </div>
                    )}
                    {visibleColumns.some((column) => column.key === 'ip_address') && (
                      <div className="flex justify-between gap-4 text-sm">
                        <span className="text-text-muted-light dark:text-text-muted-dark">
                          {t('columns.ipAddress')}
                        </span>
                        <span className="break-all text-right font-mono">
                          {log.ip_address || t('details.empty')}
                        </span>
                      </div>
                    )}
                    {log.error_message ? (
                      <p className="text-sm text-state-danger-light dark:text-state-danger-dark">
                        {log.error_message}
                      </p>
                    ) : log.details && visibleColumns.some((column) => column.key === 'context') ? (
                      <div className="border-t border-surface-border-light pt-2 dark:border-surface-border-dark">
                        <Button
                          variant="ghost"
                          className="min-h-11 w-full justify-start px-0 text-xs"
                          aria-expanded={isExpanded}
                          onClick={() => toggleRow(log.id)}
                        >
                          {isExpanded
                            ? t('details.hide', { count: detailCount })
                            : t('details.show', { count: detailCount })}
                        </Button>
                        {isExpanded && (
                          <div className="space-y-1 rounded-lg bg-surface-2-light p-2 dark:bg-surface-2-dark">
                            {Object.entries(log.details).map(([key, value]) => (
                              <div key={key} className="break-all text-xs">
                                <span className="font-medium text-text-subtle-light dark:text-text-subtle-dark">
                                  {getDetailLabel(key)}:{' '}
                                </span>
                                <span>{getDetailValue(key, value)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
}
